import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { NativeSelect, TextInput } from "@mantine/core";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Company } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PAGE_SIZE, applySorts, useListParams } from "../lib/useListParams";
import { ListShell } from "../components/ListShell";
import { EnrichmentBadge, useEnrichmentForIds } from "../components/Enrichment";
import { absent, nothingUseful, orAbsent, shortDate } from "../lib/format";
import { recallListPlace, rememberListPlace } from "../lib/listMemory";
import { downloadNarrowed } from "../lib/csvDownload";
import { applyRules } from "../lib/advancedFilters";
import { useCsvColumns } from "../lib/columnChoice";
import { RowActionForm, type RowAction } from "../components/RowActions";
import { DataTable, visibleWith, type ColumnSpec } from "../ui/DataTable";
import { RowMenu } from "../ui/Menu";

const FILTERS = ["state", "hubspot", "relevance"] as const;

const COLUMNS =
  "id,name,hq_city,hq_state,website,property_count,people_count,counted_at,source_count,newest_source_date,hubspot_status,relevance";

/* The fields a rep wants first. Everything else the view returns can be
   added from the Columns control, and the choice is remembered. */
const CSV_DEFAULT = ["name", "hq_city", "hq_state", "website", "property_count", "people_count",
             "source_count", "newest_source_date", "hubspot_status", "relevance"] as const;

/* A stored count, with when it was counted on the tooltip. */
function countedCell(n: number | null | undefined, countedAt: string | null | undefined) {
  if (n === null || n === undefined) {
    return nothingUseful("Added since the last count");
  }
  const when = countedAt ? new Date(countedAt) : null;
  const title = when && !Number.isNaN(when.getTime())
    ? `Counted at ${when.toLocaleTimeString()}`
    : "Count date unknown";
  return <span title={title}>{n.toLocaleString()}</span>;
}

export function Companies({ session }: { session: SignedIn }) {
  /* Opens on the biggest portfolios rather than alphabetically. The A-to-Z
     default put three companies called "." and a power-washing firm at the top
     of the first screen anyone sees, and Greystar's 492 properties on page 900.
     Sorting on a stored count costs 0.65s since engine migrations 069 and 070;
     deriving it used to cost 4.6s. */
  const params = useListParams("property_count", FILTERS, false, "company");
  const csvColumns = useCsvColumns("companies", "company", CSV_DEFAULT);
  const [rowOpen, setRowOpen] = useState<{ id: string | null; action: RowAction }>({ id: null, action: null });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  /* A record is a page. Opening one remembers where this list was, so Back
     returns to the same scroll position with the opened row marked, which is
     what the old panel over the list was for. The key is the list's own
     address, so a different search or page is a different place. */
  const placeKey = `companies${location.search}`;
  const [lastOpened, setLastOpened] = useState<string | null>(null);
  const restored = useRef(false);

  /* One place that says what the list is narrowed to, called by both the rows
     query and the count query. Written once rather than twice because two
     copies that drift produce "42,616 matches" above fifty rows that were
     filtered, which is a screen lying about its own contents. */
  /* `columns` is here because the CSV can carry fields the table does not show.
     Selecting the table's columns for a download that asked for others would
     write a file with the right headings and empty cells under them, which is
     worse than refusing. */
  const narrowed = (opts?: { count?: "exact"; head?: boolean }, columns?: string) => {
    /* A search reads api.company_search, which matches the name or any other name
       the record is known by (engine migration 121), and returns rows of the
       same view, so every filter, sort, page and count below works unchanged. */
    /* The function's filters go on before its column list, because in this
       client a select on a function call ends the filtering, and after the
       view's select, because on a view it begins it. */
    const s = params.search.trim();
    const client = apiClient(session.idToken);
    type Rows = ReturnType<ReturnType<typeof client.from>["select"]>;
    let q = s
      ? (client.rpc("company_search", { p_q: s }, { get: true, ...opts }) as unknown as Rows)
      : client.from("company").select(columns ?? COLUMNS, opts);
    if (params.filters["state"]) q = q.eq("hq_state", params.filters["state"]);
    if (params.filters["hubspot"]) q = q.eq("hubspot_status", params.filters["hubspot"]);
    if (params.filters["relevance"]) q = q.eq("relevance", params.filters["relevance"]);
    // The reader's own stacked filters, on any column the view returns.
    q = applyRules(q, "company", params.advanced);
    return s ? (q as unknown as { select: (c: string) => Rows }).select(columns ?? COLUMNS) : q;
  };

  const query = useQuery({
    queryKey: ["companies", params.page, params.search, params.sorts,
               params.filters, params.advanced],
    /* The rows on screen stay while the next page or the next set of criteria
       loads. docs/plan.md lists "Loading, refreshing" as a state every screen
       has to build: show the old data with a refreshing indicator, never a
       blank screen. Changing a query key makes react-query treat it as a new
       query with nothing cached, so isLoading went true and the table was
       replaced by the word Loading on every keystroke in the campaign builder
       and on every page turn everywhere else. */
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error } = await applySorts(narrowed(), params.sorts)
        // Roughly forty thousand companies tie at zero properties. Without a
        // last key Postgres may order the ties differently per request, so a
        // row can appear on two pages and another on none.
        .order("name", { ascending: true })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as Company[];
    },
  });

  /* Counting is the expensive half, so it is its own request. Measured on the
     live lake, a page of fifty reads in hundredths of a second and counting the
     whole view takes seconds, because a count has to visit every row. Asking
     for both in one request made the screen wait for the slower half. The page
     number is deliberately not in this key, so paging through a list counts
     once rather than once a page. The key starts with the same word as the rows
     query so that Refresh clears both. */
  const countQuery = useQuery({
    queryKey: ["companies", "count", params.search, params.filters, params.advanced],
    queryFn: async () => {
      const { error, count } = await narrowed({ count: "exact", head: true });
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? null;
    },
  });

  const rows = query.data ?? [];
  // Enrichment state for this page only, in one request. It is a separate view
  // rather than columns on api.company, so a page costs two requests instead of
  // three large views being rewritten to say one thing four times.
  const enrichment = useEnrichmentForIds(
    session.idToken,
    "company",
    rows.map((r) => r.id).filter((v): v is string => Boolean(v)),
  );

  const remember = (id: string) => rememberListPlace(placeKey, { scrollY: window.scrollY, openId: id });
  const open = (c: Company) => {
    if (!c.id) return;
    remember(c.id);
    navigate(`/companies/${c.id}`, { state: { from: "companies" } });
  };

  // Once the rows are on screen, go back to where the person was.
  useEffect(() => {
    if (restored.current || !rows.length) return;
    restored.current = true;
    const place = recallListPlace(placeKey);
    if (!place) return;
    setLastOpened(place.openId);
    window.scrollTo({ top: place.scrollY });
  }, [rows.length, placeKey]);

  // The older ?record= form, which links elsewhere still use, opens the page.
  if (params.openRecord) return <Navigate to={`/companies/${params.openRecord}`} replace />;

  /* The column model, once. Which fields lead on a phone is the design
     decision from the Companies design pass (docs/plans/front-end-overhaul.md):
     the name and the place lead, the counts and the enrichment state follow,
     and the newest source and the HubSpot check wait in the record. */
  const columns: ColumnSpec<Company>[] = [
    { id: "name", header: "Name", role: "leading", fields: ["name"], sort: "name", narrowLabel: null, title: true,
      /* A real link, so a rep can open five companies into tabs with a
         modifier key, which a button cannot do. The row's own click leaves
         links to themselves (src/ui/DataTable.tsx). */
      cell: (c) => c.id ? (
        <Link to={`/companies/${c.id}`} state={{ from: "companies" }} className="link" onClick={() => remember(c.id as string)}>
          {c.name}
        </Link>
      ) : c.name },
    /* City and state as their own columns, so each sorts and filters.
       James, 2026-10-08. */
    { id: "hq_city", header: "City", role: "leading", fields: ["hq_city"], sort: "hq_city", narrowLabel: null,
      cell: (c) => orAbsent(c.hq_city) },
    { id: "hq_state", header: "State", role: "leading", fields: ["hq_state"], sort: "hq_state", narrowLabel: null,
      cell: (c) => orAbsent(c.hq_state) },
    /* These two are the only stale values on the row. Engine migration 069
       stores them in lake.company_counts and a timer refreshes them every
       fifteen minutes, which is what made this screen fast. Everything else
       here is read live, so the age goes on these two cells and not on the
       row. A null count is a company added since the last refresh. It is not
       zero, and docs/screens.md says those are different facts. */
    { id: "property_count", header: "Properties", role: "secondary", fields: ["property_count"], align: "num",
      sort: "property_count", cell: (c) => countedCell(c.property_count, c.counted_at) },
    { id: "people_count", header: "People", role: "secondary", fields: ["people_count"], align: "num",
      sort: "people_count", cell: (c) => countedCell(c.people_count, c.counted_at) },
    { id: "source_count", header: "Sources", role: "secondary", fields: ["source_count"], align: "num",
      sort: "source_count", cell: (c) => c.source_count ?? absent() },
    { id: "newest_source_date", header: "Newest source", role: "detail", fields: ["newest_source_date"],
      sort: "newest_source_date", cell: (c) => shortDate(c.newest_source_date) },
    /* Never "new" for a row no crosswalk has examined. Printing new there sends
       a rep to call an existing customer. */
    { id: "hubspot_status", header: "In HubSpot", role: "detail", fields: ["hubspot_status"],
      cell: (c) => c.hubspot_status === "yes"
        ? "yes"
        : nothingUseful(c.hubspot_status === "not checked"
            ? "Not checked against HubSpot yet"
            : "Not found in HubSpot") },
    { id: "enrichment", header: "Enrichment", role: "secondary", fields: [], narrowLabel: null,
      cell: (c) => <EnrichmentBadge state={c.id ? enrichment.data?.[c.id]?.state : null} compact /> },
  ];

  return (
    <>
      <ListShell
        title="Companies"
        view="company"
        params={params}
        csvColumns={csvColumns}
        total={countQuery.data ?? null}
        totalPending={countQuery.isPending}
        rowCount={rows.length}
        isLoading={query.isLoading}
        isFetching={query.isFetching}
        isError={query.isError}
        error={query.error as Error | null}
        dataUpdatedAt={query.dataUpdatedAt}
        onRefresh={() => queryClient.invalidateQueries({ queryKey: ["companies"] })}
        onRetry={() => query.refetch()}
        onDownload={() =>
          /* The whole narrowed set, not the page. narrowed() is the same
             function the table and the count use, so a download can never be of
             something else. */
          downloadNarrowed({
            name: "companies",
            columns: csvColumns.chosen,
            total: countQuery.data ?? null,
            fetchAll: async (ceiling) => {
              const { data, error } = await applySorts(narrowed(undefined, csvColumns.chosen.join(",")), params.sorts)
                .range(0, ceiling - 1);
              if (error) throw new Error(apiErrorMessage(error));
              return (data ?? []) as Company[];
            },
          })
        }
        searchLabel="Search by name"
        emptyEverything="No companies match these filters. Change them above, or press Refresh."
        filterLabels={{ state: "State", hubspot: "HubSpot", relevance: "Relevance" }}
        filters={
          <>
            <label htmlFor="f-state">State</label>
            <TextInput
              id="f-state" defaultValue={params.filters["state"] ?? ""}
              w="4.5rem" key={params.filters["state"] ?? ""}
              onBlur={(e) => params.set({ filters: { ...params.filters, state: e.currentTarget.value.toUpperCase() } })}
              /* Enter applies it too. On blur alone a rep types CA, presses Enter,
                 and the list does not move, which reads as a filter that does not
                 work. docs/plan.md asks for Enter to submit. */
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                params.set({ filters: { ...params.filters,
                                        state: e.currentTarget.value.toUpperCase() } });
              }}
            />
            <label htmlFor="f-hubspot">HubSpot</label>
            <NativeSelect
              id="f-hubspot" value={params.filters["hubspot"] ?? ""}
              data={[
                { value: "", label: "any" },
                { value: "yes", label: "yes" },
                { value: "no", label: "no" },
                { value: "not checked", label: "not checked" },
              ]}
              onChange={(e) => params.set({ filters: { ...params.filters, hubspot: e.currentTarget.value } })}
            />
          </>
        }
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(c) => c.id ?? ""}
          params={params}
          visible={visibleWith(csvColumns.chosen)}
          openId={lastOpened}
          onOpen={open}
          rowMenu={(c) => (
            <RowMenu items={[
              { label: "Run web enrichment", title: "Search the web for this record",
                onSelect: () => setRowOpen({ id: c.id ?? null, action: "enrich" }) },
              { label: "Flag", title: "Say this record is wrong. The record stays as it is.",
                onSelect: () => setRowOpen({ id: c.id ?? null, action: "flag" }) },
            ]} />
          )}
          rowExtra={(c) => rowOpen.id === c.id && rowOpen.action ? (
            <RowActionForm
              token={session.idToken}
              entityType="company"
              entityId={c.id as string}
              action={rowOpen.action}
              onDone={() => setRowOpen({ id: null, action: null })}
            />
          ) : null}
        />
      </ListShell>
    </>
  );
}
